import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};
const stagger = { visible: { transition: { staggerChildren: 0.15 } } };

const films = [
  {
    title: "HELENE: Ignoring The Red Tape",
    description: `"Helene: Ignoring The Red Tape" is a gripping documentary uncovering the untold story of Hurricane Helene's devastating impact on Western North Carolina. This film dives deep into the aftermath of one of the most catastrophic storms to hit the region, where entire communities were wiped off the map and lives were forever changed.`,
    youtubeId: "_9qSuF98nm8",
    status: "released",
  },
  {
    title: "Senior Citizen Trafficking is Real",
    description: `We interviewed five individuals who were directly affected. Either as survivors or loved ones of victims. Their stories are real, raw, and difficult to hear, but they matter. From financial exploitation to forced relocation and abuse, this documentary uncovers a side of trafficking most people don't even know exists.`,
    youtubeId: "ZlidR_eW7Go",
    status: "released",
  },
  {
    title: "Cosmo Can't Die",
    description: `Cosmo Can't Die is the raw, emotional journey of Cosmo, a young man paralyzed in a devastating skiing accident who refused to let tragedy define him. From the moment his life changed forever to the day he stepped onto the Mr. Olympia stage as a professional wheelchair bodybuilder, this documentary follows his fight, his family's strength, and the relentless will to rise when everything feels lost.`,
    youtubeId: null,
    thumbnail: "/cosmo-photo.png",
    status: "coming_soon",
  },
];

export default function FilmsPage() {
  return (
    <div className="font-body" data-testid="films-page">
      {/* Hero */}
      <section className="pt-32 pb-16 lg:pt-40 lg:pb-24" data-testid="films-hero">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <motion.div initial="hidden" animate="visible" variants={stagger} className="max-w-3xl">
            <motion.p variants={fadeUp} className="text-xs uppercase tracking-[0.25em] text-[#C86B53] font-semibold mb-4">
              Our Films
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading text-5xl sm:text-6xl font-bold tracking-tight leading-none text-[#292524] mb-6">
              See Our Latest Films
            </motion.h1>
            <motion.p variants={fadeUp} className="text-base font-medium leading-relaxed text-[#57534E]">
              Thanks to your direct support we are able to create films that make the{" "}
              <em className="text-[#C86B53] font-semibold">invisible</em>{" "}
              <strong>visible</strong>
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Films List */}
      {films.map((film, i) => (
        <section
          key={film.title}
          className={`py-16 lg:py-24 ${i % 2 === 0 ? "bg-[#F2EDE4]" : ""}`}
          data-testid={`film-section-${i}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-12">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6 }}
            >
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                {/* Video/Coming Soon */}
                <div className={`${i % 2 === 1 ? "lg:order-2" : ""}`}>
                  {film.youtubeId ? (
                    <div className="video-container rounded-sm overflow-hidden shadow-lg" data-testid={`film-video-${i}`}>
                      <iframe
                        src={`https://www.youtube.com/embed/${film.youtubeId}`}
                        title={film.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                        referrerPolicy="strict-origin-when-cross-origin"
                        allowFullScreen
                      />
                    </div>
                  ) : film.thumbnail ? (
                    <div
                      className="relative aspect-video rounded-sm overflow-hidden bg-black"
                      data-testid={`film-coming-soon-${i}`}
                    >
                      <img src={film.thumbnail} alt={film.title} className="w-full h-full object-contain" />
                      <div className="absolute top-4 right-4">
                        <span className="bg-[#C86B53] text-white text-xs uppercase tracking-[0.2em] px-5 py-2 rounded-sm font-semibold">
                          Coming Soon
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div
                      className="relative aspect-video rounded-sm overflow-hidden bg-[#292524] flex items-center justify-center"
                      data-testid={`film-coming-soon-${i}`}
                    >
                      <div className="text-center px-8">
                        <p className="font-heading text-3xl sm:text-4xl font-bold text-white mb-4">{film.title}</p>
                        <span className="bg-[#C86B53] text-white text-xs uppercase tracking-[0.2em] px-5 py-2 rounded-sm font-semibold">
                          Coming Soon
                        </span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Info */}
                <div className={`${i % 2 === 1 ? "lg:order-1" : ""}`}>
                  {film.status === "coming_soon" && (
                    <span className="inline-block bg-[#C86B53]/10 text-[#C86B53] text-[10px] uppercase tracking-[0.15em] px-3 py-1 rounded-sm font-semibold mb-4">
                      Coming Soon
                    </span>
                  )}
                  <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[#292524] mb-6">
                    {film.title}
                  </h2>
                  <p className="text-base font-medium leading-relaxed text-[#57534E] mb-8">
                    {film.description}
                  </p>
                  <a
                    href="https://www.paypal.com/ncp/payment/3KN4GBXUVPSZL"
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`film-support-button-${i}`}
                  >
                    <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-6 h-10 text-sm font-semibold tracking-wide">
                      Support Future Projects
                      <ArrowRight size={14} strokeWidth={2} className="ml-1" />
                    </Button>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </section>
      ))}

      {/* Support CTA */}
      <section className="py-24 lg:py-32 bg-[#292524] grain-overlay relative" data-testid="films-donate-cta">
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            variants={stagger}
          >
            <motion.h2 variants={fadeUp} className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-white mb-6">
              Support Future Projects
            </motion.h2>
            <motion.p variants={fadeUp} className="text-base font-medium text-white/70 max-w-xl mx-auto mb-10">
              Your generosity funds the next story. Help us continue making the invisible visible.
            </motion.p>
            <motion.div variants={fadeUp}>
              <a
                href="https://www.paypal.com/ncp/payment/3KN4GBXUVPSZL"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="films-donate-link"
              >
                <Button className="bg-[#C86B53] hover:bg-[#b55c46] text-white rounded-sm px-10 h-12 text-sm font-semibold tracking-wide">
                  Donate Now
                  <ArrowRight size={16} strokeWidth={2} className="ml-1" />
                </Button>
              </a>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
